import { useState } from 'react';
import { Bell, ChevronDown, Menu, LogOut, Box, Check, Repeat, SlidersHorizontal } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { CachedAvatar } from '@/components/ui/CachedAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/DropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Breadcrumbs, type BreadcrumbItem } from '@/components/shared';

import { useAuthStore } from '@/features/auth/store/authStore';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { useSwitchCompany } from '@/features/auth/hooks/useSwitchCompany';

type HeaderBarProps = {
  onMenuClick?: () => void;
  breadcrumbs?: {
    title?: string;
    items: BreadcrumbItem[];
  };
  showLogo?: boolean;
};

export const HeaderBar = ({ onMenuClick, breadcrumbs, showLogo }: HeaderBarProps) => {
  const navigate = useNavigate();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken));
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const user = useAuthStore((state) => state.user);
  const logoutMutation = useLogout();
  const switchCompanyMutation = useSwitchCompany();

  const handleLogin = () => {
    navigate('/login');
  };

  const performLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const openLogoutDialog = () => {
    setIsLogoutDialogOpen(true);
  };

  const closeLogoutDialog = () => {
    setIsLogoutDialogOpen(false);
  };

  const confirmLogout = () => {
    setIsLoggingOut(true);

    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        performLogout();
        setIsLogoutDialogOpen(false);
        setIsLoggingOut(false);
      },
      onError: () => {
        // Even if API call fails, still logout locally
        performLogout();
        setIsLogoutDialogOpen(false);
        setIsLoggingOut(false);
      },
    });
  };

  const handleSwitchCompany = (companyId: string | number) => {
    switchCompanyMutation.mutate(companyId);
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 bg-surface-0 px-4 sm:px-6 lg:px-8">
      {showLogo ? (
        <Link to="/my-tasks" className="flex items-center mr-auto">
          <img
            src="/prima-larvae-logo.svg"
            alt="Prima Larvae Bali"
            className="h-8 w-auto sm:h-10"
          />
        </Link>
      ) : (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-lg border border-border-default bg-surface-0 lg:hidden"
            onClick={onMenuClick}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5 text-text-secondary" />
          </Button>
          <div className="hidden flex-1 items-center lg:flex">
            {breadcrumbs && <Breadcrumbs title={breadcrumbs.title} items={breadcrumbs.items} />}
          </div>
        </>
      )}
      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-lg bg-surface-0 transition-colors duration-normal ease-standard hover:bg-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 ring-offset-background"
        >
          <Bell className="h-5 w-5 text-neutral-600" />
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-600 ring-2 ring-surface-0" />
        </Button>

        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 rounded-lg bg-surface-0 px-3 py-2 transition-all duration-normal ease-standard hover:-translate-y-0.5 hover:bg-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 ring-offset-background cursor-pointer"
              >
                <CachedAvatar
                  fileId={user?.avatar_file_id}
                  fallbackUrl={user?.avatar_url}
                  userName={user?.name || 'User'}
                  className="h-10 w-10 ring-2 ring-primary/15"
                />
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-semibold text-neutral-950 leading-tight">
                    {user?.name || 'User'}
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 text-neutral-600" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-80 rounded-xl border border-border-default bg-surface-0/95 p-2 shadow-xl backdrop-blur-md"
            >
              <div className="flex items-center gap-3 px-4 py-4">
                <CachedAvatar
                  fileId={user?.avatar_file_id}
                  fallbackUrl={user?.avatar_url}
                  userName={user?.name || 'User'}
                  className="h-12 w-12 ring-2 ring-primary/15"
                />
                <div className="flex flex-col">
                  <p className="text-base font-semibold leading-none text-neutral-950">{user?.name || 'User'}</p>
                  <p className="text-sm text-neutral-600 mt-1">
                    {user?.email || 'No email'}
                  </p>
                </div>
              </div>

              <DropdownMenuSeparator className="bg-border-subtle my-1" />

              <div className="px-2 py-2">
                <div className="flex items-center justify-between rounded-lg bg-surface-1/50 px-3 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black text-white">
                      <Box className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-medium text-neutral-950">{user?.selected_company?.name || 'No Company'}</span>
                  </div>
                  <Check className="h-5 w-5 text-primary-500" />
                </div>
              </div>

              <DropdownMenuSeparator className="bg-border-subtle my-1" />

              <div className="p-1">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-3 text-neutral-950 transition-colors hover:bg-surface-1 focus:bg-surface-1 data-[state=open]:bg-surface-1">
                    <Repeat className="h-5 w-5 text-neutral-950" />
                    <span className="text-sm font-medium">Switch company</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64 rounded-lg border border-border-default bg-surface-0 p-1 shadow-xl" sideOffset={8}>
                    <div className="px-3 py-2">
                      <p className="text-xs text-neutral-600">{user?.email}</p>
                    </div>
                    {user?.companies?.map((company) => {
                      const isSelected = company.id === user?.selected_company?.id;
                      return (
                        <DropdownMenuItem
                          key={company.id}
                          onClick={() => !isSelected && handleSwitchCompany(company.id)}
                          disabled={isSelected}
                          className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-neutral-950 focus:bg-surface-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="text-sm font-medium">{company.name}</span>
                          {isSelected && <Check className="h-5 w-5 text-primary-500" />}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuItem
                  onClick={handleSettings}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-3 text-neutral-950 transition-colors hover:bg-surface-1 focus:bg-surface-1"
                >
                  <SlidersHorizontal className="h-5 w-5 text-neutral-950" />
                  <span className="text-sm font-medium">Settings</span>
                </DropdownMenuItem>
              </div>

              <DropdownMenuSeparator className="bg-border-subtle my-1" />

              <div className="p-1">
                <DropdownMenuItem
                  onSelect={openLogoutDialog}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-3 text-neutral-950 transition-colors hover:bg-surface-1 focus:bg-surface-1"
                >
                  <LogOut className="h-5 w-5 text-neutral-950" />
                  <span className="text-sm font-medium">Logout</span>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button onClick={handleLogin} className="rounded-lg">
            Login
          </Button>
        )}
      </div>
      <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
        <DialogContent className="max-w-md rounded-lg border border-border-default/60 bg-surface-0/95 px-8 py-8 shadow-[0_28px_80px_rgba(15,23,42,0.16)] backdrop-blur-sm">
          <DialogHeader className="space-y-6 text-left">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-red-50 text-danger">
              <LogOut className="h-6 w-6" />
            </div>
            <div className="space-y-3">
              <DialogTitle className="text-xl font-semibold tracking-tight text-neutral-950">
                Log out?
              </DialogTitle>
              <DialogDescription className="text-base leading-relaxed text-neutral-600">
                You will be signed out and redirected to the login page. Make sure your work is saved.
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4">
            <Button
              variant="outline"
              onClick={closeLogoutDialog}
              disabled={isLoggingOut}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? "Logging out..." : "Log out"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
